import { ButtonHTMLAttributes, forwardRef, ReactNode, useCallback, useId, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import styles from "./Button.module.css";
import { cx } from "../utils";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "success";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    children?: ReactNode;
    variant?: ButtonVariant;
    size?: ButtonSize;
    isLoading?: boolean;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    iconOnly?: boolean;
    iconShape?: "circle" | "square";
    fullWidth?: boolean;
}

const sizeClasses: Record<ButtonSize, string> = {
    xs: styles.sizeXs,
    sm: styles.sizeSm,
    md: styles.sizeMd,
    lg: styles.sizeLg
};

const variantClasses: Record<ButtonVariant, string> = {
    primary: styles.primary,
    secondary: styles.secondary,
    ghost: styles.ghost,
    danger: styles.danger,
    success: styles.success
};

// forwardRef so composition wrappers (e.g. Radix Menu.Trigger asChild) can
// reach the real <button>; without it those triggers silently never open.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        children,
        variant = "secondary",
        size = "md",
        isLoading = false,
        leftIcon,
        rightIcon,
        iconOnly = false,
        iconShape = "circle",
        fullWidth = false,
        className,
        disabled,
        type = "button",
        onClick,
        ...props
    },
    forwardedRef
) {
    const isDisabled = disabled || isLoading;
    const reducedMotion = useReducedMotion();
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const setRefs = useCallback(
        (node: HTMLButtonElement | null) => {
            buttonRef.current = node;
            if (typeof forwardedRef === "function") forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
        },
        [forwardedRef]
    );
    const filterId = useId().replace(/:/g, "-");
    const [ripple, setRipple] = useState<{ x: number; y: number } | null>(null);

    const handleClick = useCallback<NonNullable<ButtonHTMLAttributes<HTMLButtonElement>["onClick"]>>(
        event => {
            if (isDisabled) {
                return;
            }

            if (!reducedMotion && buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setRipple({ x: event.clientX - rect.left, y: event.clientY - rect.top });
                window.setTimeout(() => setRipple(null), 700);
            }

            onClick?.(event);
        },
        [isDisabled, onClick, reducedMotion]
    );

    const rootClassName = cx(
        styles.button,
        variantClasses[variant],
        sizeClasses[size],
        iconOnly && (iconShape === "square" ? styles.iconSquare : styles.iconOnly),
        fullWidth && styles.fullWidth,
        isDisabled && styles.disabled,
        className
    );

    return (
        <button
            {...props}
            ref={setRefs}
            type={type}
            className={rootClassName}
            disabled={isDisabled}
            onClick={handleClick}
        >
            <svg className={styles.svgFilter} aria-hidden="true">
                <defs>
                    <filter id={`lg-dist-${filterId}`}>
                        <feTurbulence type="fractalNoise" baseFrequency="0.015" numOctaves="3" seed="1" result="noise" />
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="6" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                </defs>
            </svg>

            <span className={styles.filterLayer} style={{ filter: `url(#lg-dist-${filterId})` }} />
            <span className={styles.overlay} />
            <span className={styles.specular} />

            {ripple ? <span className={styles.ripple} style={{ left: ripple.x, top: ripple.y }} /> : null}

            <span className={styles.content}>
                {isLoading ? (
                    <svg className={styles.spinner} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className={styles.spinnerTrack} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                            className={styles.spinnerHead}
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                    </svg>
                ) : null}
                {!isLoading && leftIcon ? <span className={styles.icon}>{leftIcon}</span> : null}
                {children ? <span>{children}</span> : null}
                {!isLoading && rightIcon ? <span className={styles.icon}>{rightIcon}</span> : null}
            </span>
        </button>
    );
});

export default Button;
